import { createUseCase } from '@brickflow/http'

import type { useLocale } from '@/composables/locale'
import type { SocketClient } from '@/core/transports'

import { Query } from '@/core/constants/route'
import { storageService } from '@/core/services'
import { createGet } from '@/core/util'

import type { Tournament } from '../tournament/entity'
import type { RootFeedbackCategory, RootInitResponse } from './entity/base'

function isRootInitSuccess(
  value: null | RootInitResponse | undefined,
): value is Extract<RootInitResponse, { status: 'success' }> {
  return Boolean(value && value.status === 'success')
}

export default createUseCase<{
  currency: ReturnType<typeof useCurrency>
  dialog: ReturnType<typeof useDialog>
  localeInst: ReturnType<typeof useLocale>
  rootStore: ReturnType<typeof useBrickRootStore>
  route: ReturnType<typeof useRoute>
  router: ReturnType<typeof useRouter>
  socketClient: SocketClient
  tournamentsStore: ReturnType<typeof useBrickTournamentsStore>
  userStore: ReturnType<typeof useBrickUserStore>
  vipStore: ReturnType<typeof useBrickVipStore>
}>()(
  ({
    currency,
    dialog,
    httpClient,
    localeInst,
    rootStore,
    route,
    router,
    socketClient,
    tournamentsStore,
    userStore,
    vipStore,
  }) => ({
    bannerList: createGet()('banners/list'),
    async closeBanner(id: number) {
      const result = await httpClient.post(`banners/click/${id}`)
      return result.data
    },
    confirmEmail: createGet<{ redirect: 'manual' }>()(`confirmation/:type/:token`),
    getAuthCredentials: createGet()(`user/fast_auth_credentials`), // P.S unused method
    getInitialCurrency: createGet()(`country_currency/map`),

    getPageMeta: createGet<{ is_game?: string; page: string }>()('seo/page_meta'),

    // init(options?: OptionsHttp<typeof rootInitResponseSchema>) {
    init: createGet<{ auth_token?: string }>()('main_page', {
      effect(data, httpConfig) {
        async function getAuthCredentials(): Promise<{
          email: null | string
          generated: boolean
          password: null | string
        }> {
          const result = await httpClient.get('user/fast_auth_credentials')
          return result.data
        }

        try {
          const startTime = Date.now()
          const initData = data
          const endTime = Date.now()
          rootStore.setInitReqTime(endTime - startTime)

          const notify = useNotify()

          if (isRootInitSuccess(initData)) {
            const clearAuthTokenQuery = (): void => {
              const currentAuthToken = route.query[Query.AuthToken]
              const requestAuthToken =
                typeof httpConfig.params === 'object' && httpConfig.params !== null
                  ? (httpConfig.params as Record<string, unknown>)[Query.AuthToken]
                  : undefined

              if (
                !initData.user ||
                typeof currentAuthToken !== 'string' ||
                typeof requestAuthToken !== 'string' ||
                currentAuthToken !== requestAuthToken
              ) {
                return
              }

              router.replace({
                path: route.path,
                query: {
                  ...route.query,
                  [Query.AuthToken]: undefined,
                },
              })
            }

            const signUpSessionKey =
              import.meta.client && initData.user?.id && initData.gtm?.signUp
                ? `signup-handled:${initData.user.id}:${initData.gtm.signUp}:${initData.gtm.is_fast_auth ? 'fast' : 'default'}`
                : null
            const shouldHandleSignUp = import.meta.client
              ? !signUpSessionKey || !sessionStorage.getItem(signUpSessionKey)
              : true

            if (initData.config.server_time) {
              const serverTime = new Date(initData.config.server_time.time).getTime()
              const serverClientTime = serverTime + (endTime - startTime) / 2
              rootStore.setServerTimeDifference(endTime - serverClientTime)
            }

            rootStore.setToken(initData.token)

            if (!httpConfig.cached) {
              if (initData.error_message) {
                notify.create({
                  title: initData.error_message,
                  type: 'error',
                })
              }
              if (initData.success_message) {
                if (initData.success_message === 'payment.message_success') {
                  notify.create({
                    description: initData.success_message,
                    title: 'success',
                    type: 'success',
                  })
                } else {
                  notify.create({
                    title: initData.success_message,
                    type: 'success',
                  })
                }
              }

              const purchases = initData.gtm?.purchases
              if (Array.isArray(purchases)) {
                for (const purchase of purchases) {
                  const purchaseData = {
                    currency: purchase.currency,
                    items: [
                      {
                        item_id: purchase.user_order_id,
                        item_name: purchase.payment_system_name,
                        price: Number(purchase.amount),
                        quantity: 1,
                      },
                    ],
                    transaction_id: purchase.user_order_id,
                    user_id: initData.user?.id,
                    value: Number(purchase.amount),
                  }
                  if (import.meta.client) {
                    globalThis?.dataLayer?.push({
                      event: 'purchase_front',
                      eventModel: purchaseData,
                    })
                  }
                }
              }
              if (import.meta.client && initData.gtm?.signIn) {
                globalThis?.dataLayer?.push({
                  event: 'complete-login',
                  eventModel: {
                    type: initData.gtm.signIn,
                    user_id: initData.user?.id,
                  },
                })
                if (sessionStorage.getItem('AuthRedirect')) {
                  const fullPath = sessionStorage.getItem('AuthRedirect') || ''
                  sessionStorage.removeItem('AuthRedirect')
                  navigateTo(localeInst.localePath(fullPath), { external: true })
                }
              }
              if (import.meta.client && initData.gtm?.signUp && shouldHandleSignUp) {
                if (signUpSessionKey) {
                  sessionStorage.setItem(signUpSessionKey, '1')
                }
                globalThis?.dataLayer?.push({
                  event: 'complete-registration',
                  eventModel: {
                    type: initData.gtm.signUp,
                    user_id: initData.user?.id,
                  },
                })
                if (initData.gtm?.is_fast_auth) {
                  const flow = sessionStorage.getItem('flow')
                  if (flow === undefined) {
                    router.replace(localeInst.localePath('/wallet/deposit'))
                  }
                  onNuxtReady(async () => {
                    const authResult = await getAuthCredentials()
                    if (authResult.generated) {
                      const openDialog = async (): Promise<void> => {
                        await dialog.open('generatedAuthInfo', {
                          email: authResult.email,
                          password: authResult.password,
                        })
                        const dialogResult = await dialog.open('generatedAuthInfoConfirm')
                        if (dialogResult === 'cancel') {
                          await openDialog()
                        }
                      }
                      await openDialog()
                      if (flow === 'wallet') {
                        dialog.open('fastDeposit')
                      }
                    }
                  })
                } else {
                  const flow = sessionStorage.getItem('flow')
                  if (flow === 'wallet' || localeInst.getRealName(route) === 'sport-sport') {
                    dialog.open('fastDeposit')
                  } else {
                    router.replace(localeInst.localePath('/wallet/deposit'))
                  }
                }
                storageService.set('first-deposit', true)
              }
              if (import.meta.client && (initData.gtm?.signUp || initData.gtm?.signIn)) {
                storageService.remove('force_promocode_login')
              }

              if (import.meta.client) {
                requestAnimationFrame(clearAuthTokenQuery)
              }
            }
            rootStore.setConfig(initData.config)
            rootStore.setSignedSession(initData.signed_session)
            rootStore.setSignedSessionBB(initData.signed_session_bb)
            rootStore.setPowChallenge(initData.pow_challenge)
            rootStore.setFeatures(initData.features)
            if (initData.config?.wheel_spin?.user_spins_count) {
              rootStore.setSpinCount(initData.config.wheel_spin.user_spins_count)
            }
            rootStore.setGames(initData.games)
            userStore.setCached(httpConfig.cached)
            userStore.rewardsCount = initData.config?.rewards?.active_and_pending_count ?? 0
            if (!httpConfig.cached) {
              userStore.setUser(initData.user)
              userStore.setLoading(false)
            }
            if (initData.config.tournaments) {
              tournamentsStore.updateListPreview(initData.config.tournaments as Record<string, Tournament>)
            }
            if (initData.user) {
              globalThis?.dataLayer?.push({
                event: 'configure',
                eventModel: {
                  user_id: initData.user.id,
                },
              })
              if (initData.user.user_vip_club_progress) {
                vipStore.update(initData.user.user_vip_club_progress)
              }
              vipStore.setCurrentLevel(initData.user?.user_vip_club_progress?.current_level || 0)
              userStore.setCurrentCurrency(initData.user.user_currency)
              vipStore.setRequiredForNextLevel(initData.user?.user_vip_club_progress?.required_for_next_level || 0)
              vipStore.setWagerAmount(initData.user?.user_vip_club_progress?.wager_amount || 0)
              vipStore.setRequiredForCurrentLevel(
                initData.user?.user_vip_club_progress?.required_for_current_level || 0,
              )
              if (initData.user?.email_state === 'confirmed') {
                storageService.set('user_email_confirmed', true)
              }
            }
            if (!initData.user) {
              const manualSelectedCurrency = storageService.get('manual-selected-currency')
              if (manualSelectedCurrency) {
                userStore.setCurrentCurrency(currency.getCurrencyByCode(manualSelectedCurrency))
              }
            }

            if (userStore.isAuth && initData.error_message === 'check_kyc_input_fields') {
              dialog.open('kycPartial', {
                documentRequirements: ['CUIT'],
                requiredFields: ['documents'],
              })
            }
          }

          if (storageService.get('user_email_confirmed_notification')) {
            storageService.remove('user_email_confirmed_notification')
            notify.create({
              description: 'profile.rewards.email_confirmed',
              title: 'main.success',
              type: 'success',
            })
          }

          socketClient?.reset()
        } catch (error) {
          console.error(error)
        }
      },
    }),

    powChallenge: createGet()('pow_challenge'),

    async submitFeedback({
      category,
      rating,
      text,
      type,
    }: {
      category?: RootFeedbackCategory
      rating?: number
      text: string
      type: string
    }) {
      const result = await httpClient.post('suggest/send', { category, rating, text, type })
      return result.data
    },
  }),
)
